/**
 * LabTraceabilityPage — Issue #399.
 *
 * 实验室质检 / 物料批号追溯 read-model 管理页：
 *   - 列表（最新快照）+ 生成 + 复核 + 归档 + 导出
 *   - 详情（汇总指标 + 细胞批次明细 + 物料批次明细 + 数据源接入状态）
 *   - 物料 → 细胞 / 细胞 → 物料 反查（filter / detail JSON 列表）
 *
 * 约束:
 *   - 所有数据通过 @jinzhuan/api-client；严禁前端伪造快照、状态或聚合。
 *   - 后端若以 `[restricted]` 返回（无 :reveal），前端展示「需 reveal」占位。
 *   - 导出走 authenticated fetch；CSV 首行含审计水印（后端写入）。
 *   - 未授予 :read_full 时后端返回空页 / 403；前端显示「未授权查看全租户追溯」。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  canArchiveLabTraceability,
  canExportLabTraceability,
  canGenerateLabTraceability,
  canRevealLabTraceability,
  canReviewLabTraceability,
  canSeeLabTraceability,
} from '@/lib/labTraceabilityPermissions';
import {
  downloadLabTraceabilityExport,
  useFilterLabTraceabilityCellBatches,
  useFilterLabTraceabilityMaterialBatches,
  useGenerateLabTraceabilitySnapshot,
  useLabTraceabilitySnapshotDetail,
  useLabTraceabilitySnapshots,
  useLabTraceabilitySourceStatus,
  type LabTraceabilityCellBatchRowDto,
  type LabTraceabilityMaterialBatchRowDto,
} from '@/hooks/useLabTraceability';

const RESTRICTED_MARKER = '[restricted]';

function maskBadge(value: string | undefined): React.ReactNode {
  if (!value) return <span className="text-gray-400">—</span>;
  if (value === RESTRICTED_MARKER) {
    return <span className="text-xs italic text-amber-600">[需 reveal]</span>;
  }
  return <span className="tabular-nums">{value}</span>;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'connected' || status === 'all_connected') {
    return <span className="text-xs font-medium text-green-700">{status}</span>;
  }
  if (status === 'partial' || status === 'pending_source' || status === 'pending') {
    return <span className="text-xs font-medium text-amber-600">{status}</span>;
  }
  if (status === 'recalled' || status === 'excursion') {
    return <span className="text-xs font-medium text-red-600">{status}</span>;
  }
  return <span className="text-xs font-medium text-gray-500">{status}</span>;
}

function SeverityBadge({ severity }: { severity: string | undefined }) {
  if (!severity) return <span className="text-xs text-gray-400">—</span>;
  if (severity === 'critical') {
    return <span className="text-xs font-medium text-red-700">critical</span>;
  }
  if (severity === 'high') {
    return <span className="text-xs font-medium text-red-600">high</span>;
  }
  if (severity === 'medium') {
    return <span className="text-xs font-medium text-amber-600">medium</span>;
  }
  return <span className="text-xs font-medium text-gray-600">{severity}</span>;
}

interface CellRowsTableProps {
  rows: ReadonlyArray<LabTraceabilityCellBatchRowDto>;
}

function CellRowsTable({ rows }: CellRowsTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">
        快照内暂无细胞批次记录
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left">批号</th>
            <th className="px-3 py-2 text-left">类型</th>
            <th className="px-3 py-2 text-left">状态</th>
            <th className="px-3 py-2 text-left">客户短码</th>
            <th className="px-3 py-2 text-right">实际细胞数</th>
            <th className="px-3 py-2 text-right">样本</th>
            <th className="px-3 py-2 text-right">质检</th>
            <th className="px-3 py-2 text-right">放行</th>
            <th className="px-3 py-2 text-right">回输</th>
            <th className="px-3 py-2 text-right">异常</th>
            <th className="px-3 py-2 text-left">最高严重级</th>
            <th className="px-3 py-2 text-right">关联物料</th>
            <th className="px-3 py-2 text-left">数据源</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-3 py-2 font-medium">{r.batchNo}</td>
              <td className="px-3 py-2">{r.batchType}</td>
              <td className="px-3 py-2">{r.batchStatus}</td>
              <td className="px-3 py-2">{maskBadge(r.customerCode)}</td>
              <td className="px-3 py-2 text-right">
                {maskBadge(r.actualCellCount)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.sampleCount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.qcPassCount}/{r.qcCount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.releasedCount}/{r.releaseCount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.infusionCompletedCount}/{r.infusionCount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.exceptionCount}
              </td>
              <td className="px-3 py-2">
                <SeverityBadge severity={r.exceptionHighestSeverity} />
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.materialBatchCount}
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={r.sourceStatus} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface MaterialRowsTableProps {
  rows: ReadonlyArray<LabTraceabilityMaterialBatchRowDto>;
}

function MaterialRowsTable({ rows }: MaterialRowsTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">
        快照内暂无物料批次记录
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left">物料代码</th>
            <th className="px-3 py-2 text-left">批号</th>
            <th className="px-3 py-2 text-left">供应商</th>
            <th className="px-3 py-2 text-left">效期</th>
            <th className="px-3 py-2 text-right">距效期(天)</th>
            <th className="px-3 py-2 text-right">库存量</th>
            <th className="px-3 py-2 text-left">物料状态</th>
            <th className="px-3 py-2 text-left">冷链</th>
            <th className="px-3 py-2 text-left">召回</th>
            <th className="px-3 py-2 text-right">影响批次</th>
            <th className="px-3 py-2 text-right">关联异常</th>
            <th className="px-3 py-2 text-left">最高严重级</th>
            <th className="px-3 py-2 text-left">数据源</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-3 py-2 font-medium">{r.materialCode}</td>
              <td className="px-3 py-2">{r.lotNo}</td>
              <td className="px-3 py-2">{r.vendor ?? '—'}</td>
              <td className="px-3 py-2 text-xs">
                {r.expiryDate
                  ? new Date(r.expiryDate).toISOString().slice(0, 10)
                  : '—'}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.daysUntilExpiry ?? '—'}
              </td>
              <td className="px-3 py-2 text-right">
                {maskBadge(r.quantityOnHand)}
              </td>
              <td className="px-3 py-2">{r.materialStatus}</td>
              <td className="px-3 py-2">
                <StatusBadge status={r.coldChainStatus} />
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={r.recallStatus} />
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.affectedCellBatchCount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.affectedExceptionCount}
              </td>
              <td className="px-3 py-2">
                <SeverityBadge severity={r.affectedHighestSeverity} />
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={r.sourceStatus} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function LabTraceabilityPage() {
  const { permissions } = useAuth();
  const canSee = canSeeLabTraceability(permissions);
  const canGenerate = canGenerateLabTraceability(permissions);
  const canReview = canReviewLabTraceability(permissions);
  const canArchive = canArchiveLabTraceability(permissions);
  const canExport = canExportLabTraceability(permissions);
  const canReveal = canRevealLabTraceability(permissions);

  const snapshots = useLabTraceabilitySnapshots();
  const detail = useLabTraceabilitySnapshotDetail();
  const sourceStatus = useLabTraceabilitySourceStatus();
  const generate = useGenerateLabTraceabilitySnapshot();
  const cellFilter = useFilterLabTraceabilityCellBatches();
  const materialFilter = useFilterLabTraceabilityMaterialBatches();

  // Destructure stable callbacks (each `useCallback(_, [])` in hook) so
  // effect deps never see the recreated hook-return object identity.
  const { fetchList: fetchSnapshotList } = snapshots;
  const { fetchDetail } = detail;
  const { fetchStatus } = sourceStatus;
  const { generate: runGenerate } = generate;
  const { fetchRows: fetchCellRows } = cellFilter;
  const { fetchRows: fetchMaterialRows } = materialFilter;

  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(
    null,
  );
  const [generateNotes, setGenerateNotes] = useState<string>('');
  const [exportReason, setExportReason] = useState<string>('');
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('csv');
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [batchNoFilter, setBatchNoFilter] = useState<string>('');
  const [lotNoFilter, setLotNoFilter] = useState<string>('');

  // Initial list load
  useEffect(() => {
    if (canSee) {
      fetchSnapshotList({ page: 1, limit: 20 });
    }
  }, [canSee, fetchSnapshotList]);

  // Auto-select first snapshot when list loads (and none selected yet)
  useEffect(() => {
    if (!selectedSnapshotId && snapshots.data?.data && snapshots.data.data.length > 0) {
      const first = snapshots.data.data[0];
      if (first) {
        setSelectedSnapshotId(first.id);
      }
    }
  }, [snapshots.data, selectedSnapshotId]);

  // Load detail / source status when selection changes
  useEffect(() => {
    if (selectedSnapshotId) {
      fetchDetail(selectedSnapshotId);
      fetchStatus(selectedSnapshotId);
    }
  }, [selectedSnapshotId, fetchDetail, fetchStatus]);

  const handleGenerate = useCallback(async () => {
    const created = await runGenerate(generateNotes.trim());
    if (created) {
      await fetchSnapshotList({ page: 1, limit: 20 });
      setSelectedSnapshotId(created.id);
      setGenerateNotes('');
    }
  }, [runGenerate, generateNotes, fetchSnapshotList]);

  const handleExport = useCallback(async () => {
    if (!selectedSnapshotId) return;
    setExporting(true);
    setExportError(null);
    try {
      await downloadLabTraceabilityExport({
        snapshotId: selectedSnapshotId,
        reason: exportReason || '日常追溯审计',
        format: exportFormat,
      });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }, [exportFormat, exportReason, selectedSnapshotId]);

  const handleApplyCellFilter = useCallback(() => {
    if (!selectedSnapshotId) return;
    fetchCellRows(selectedSnapshotId, {
      batchNo: batchNoFilter.trim() || undefined,
    });
  }, [batchNoFilter, fetchCellRows, selectedSnapshotId]);

  const handleApplyMaterialFilter = useCallback(() => {
    if (!selectedSnapshotId) return;
    fetchMaterialRows(selectedSnapshotId, {
      lotNo: lotNoFilter.trim() || undefined,
    });
  }, [lotNoFilter, fetchMaterialRows, selectedSnapshotId]);

  // Display rows: filtered if a search was applied, otherwise detail rows.
  const displayedCellRows = useMemo(
    () => cellFilter.data?.data ?? detail.data?.cellBatchRows ?? [],
    [cellFilter.data, detail.data],
  );
  const displayedMaterialRows = useMemo(
    () => materialFilter.data?.data ?? detail.data?.materialBatchRows ?? [],
    [materialFilter.data, detail.data],
  );

  if (!canSee) {
    return (
      <div className="p-6">
        <div className="rounded border border-amber-200 bg-amber-50 p-4 text-amber-700">
          当前账号未授予 lab.traceability:read 或 lab.traceability:read_full，无法查看实验室追溯。
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          实验室质检 / 物料批号追溯
        </h1>
        <p className="text-sm text-gray-600">
          细胞批次 → 物料批次 / 物料批次 → 细胞批次反查，含质检 / 放行 / 回输 / 异常聚合。本页面只读源记录；状态写入仍由各源模块负责。
        </p>
        {!canReveal && (
          <p className="text-xs text-amber-600">
            当前账号未授予 :reveal — 客户短码、细胞数、剂量、库存量将显示为「需 reveal」。
          </p>
        )}
      </header>

      <section className="space-y-3 rounded border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-600">备注</label>
            <input
              type="text"
              value={generateNotes}
              onChange={(e) => setGenerateNotes(e.target.value)}
              placeholder="例：周末质检追溯快照"
              className="mt-1 w-72 rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
          <button
            type="button"
            disabled={!canGenerate || generate.loading}
            onClick={handleGenerate}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {generate.loading ? '生成中…' : '生成新快照'}
          </button>
          <button
            type="button"
            onClick={() => fetchSnapshotList({ page: 1, limit: 20 })}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700"
          >
            刷新列表
          </button>
        </div>
        {generate.error && (
          <div className="text-sm text-red-600">{generate.error.message}</div>
        )}
      </section>

      <section className="rounded border border-gray-200 bg-white shadow-sm">
        <header className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold">快照列表</h2>
          {snapshots.loading && (
            <span className="text-xs text-gray-500">加载中…</span>
          )}
        </header>
        {snapshots.error && (
          <div className="px-4 py-3 text-sm text-red-600">
            {snapshots.error.message}
          </div>
        )}
        {snapshots.data && snapshots.data.data.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-gray-500">
            尚无快照。请用上方「生成新快照」按钮生成。
          </div>
        )}
        {snapshots.data && snapshots.data.data.length > 0 && (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">id</th>
                <th className="px-3 py-2 text-left">快照时间</th>
                <th className="px-3 py-2 text-left">状态</th>
                <th className="px-3 py-2 text-left">数据源</th>
                <th className="px-3 py-2 text-left">备注</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {snapshots.data.data.map((s) => (
                <tr
                  key={s.id}
                  className={
                    s.id === selectedSnapshotId
                      ? 'bg-blue-50'
                      : 'bg-white hover:bg-gray-50'
                  }
                >
                  <td className="px-3 py-2 font-mono text-xs">{s.id}</td>
                  <td className="px-3 py-2">
                    {new Date(s.snapshotTakenAt).toLocaleString('zh-CN')}
                  </td>
                  <td className="px-3 py-2">{s.status}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={s.sourceStatus} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {s.notes ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setSelectedSnapshotId(s.id)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      查看
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selectedSnapshotId && detail.data && (
        <section className="space-y-4 rounded border border-gray-200 bg-white p-4 shadow-sm">
          <header className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold">
              快照 {detail.data.id} 详情
            </h2>
            <div className="flex items-center gap-2">
              {canReview && (
                <span className="text-xs text-gray-500">
                  当前账号可复核（接口待 wiring 至控制面）
                </span>
              )}
              {canArchive && (
                <span className="text-xs text-gray-500">可归档</span>
              )}
            </div>
          </header>

          {detail.data.totals && (
            <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4 lg:grid-cols-6">
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">细胞批次</div>
                <div className="text-lg font-semibold tabular-nums">
                  {detail.data.totals.cellBatchCount}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">物料批次</div>
                <div className="text-lg font-semibold tabular-nums">
                  {detail.data.totals.materialBatchCount}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">质检失败</div>
                <div className="text-lg font-semibold tabular-nums text-red-600">
                  {detail.data.totals.qcFailCount}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">异常事件</div>
                <div className="text-lg font-semibold tabular-nums text-amber-700">
                  {detail.data.totals.exceptionCount}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">召回物料</div>
                <div className="text-lg font-semibold tabular-nums text-red-700">
                  {detail.data.totals.recalledMaterialCount}
                </div>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <div className="text-xs text-gray-500">已过期物料</div>
                <div className="text-lg font-semibold tabular-nums">
                  {detail.data.totals.expiredMaterialCount}
                </div>
              </div>
            </div>
          )}

          {canExport && (
            <div className="flex flex-wrap items-end gap-2 rounded border border-blue-100 bg-blue-50 p-3">
              <div>
                <label className="block text-xs text-gray-600">导出原因</label>
                <input
                  type="text"
                  value={exportReason}
                  onChange={(e) => setExportReason(e.target.value)}
                  placeholder="例：质检追溯月度审计"
                  className="mt-1 w-72 rounded border border-gray-300 px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600">格式</label>
                <select
                  value={exportFormat}
                  onChange={(e) =>
                    setExportFormat(e.target.value as 'csv' | 'xlsx')
                  }
                  className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
                >
                  <option value="csv">CSV</option>
                  <option value="xlsx">XLSX</option>
                </select>
              </div>
              <button
                type="button"
                disabled={exporting}
                onClick={handleExport}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:bg-gray-300"
              >
                {exporting ? '导出中…' : '导出（含水印 + 审计）'}
              </button>
              {exportError && (
                <span className="text-xs text-red-600">{exportError}</span>
              )}
            </div>
          )}

          {sourceStatus.data && (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              {sourceStatus.data.sources.map((s) => (
                <div
                  key={s.sourceType}
                  className="rounded border border-gray-200 p-2 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">
                      {s.displayLabel}
                    </span>
                    <StatusBadge status={s.status} />
                  </div>
                  {s.reason && (
                    <div className="mt-1 text-xs text-gray-500">{s.reason}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <h3 className="mr-auto text-sm font-semibold">细胞批次明细</h3>
              <div>
                <label className="block text-xs text-gray-600">按批号过滤</label>
                <input
                  type="text"
                  value={batchNoFilter}
                  onChange={(e) => setBatchNoFilter(e.target.value)}
                  className="mt-1 w-60 rounded border border-gray-300 px-2 py-1 text-sm"
                  placeholder="精确匹配 batchNo"
                />
              </div>
              <button
                type="button"
                onClick={handleApplyCellFilter}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700"
              >
                查询
              </button>
            </div>
            {cellFilter.error && (
              <div className="text-sm text-red-600">
                {cellFilter.error.message}
              </div>
            )}
            <CellRowsTable rows={displayedCellRows} />
          </div>

          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <h3 className="mr-auto text-sm font-semibold">物料批次明细</h3>
              <div>
                <label className="block text-xs text-gray-600">按批号过滤</label>
                <input
                  type="text"
                  value={lotNoFilter}
                  onChange={(e) => setLotNoFilter(e.target.value)}
                  className="mt-1 w-60 rounded border border-gray-300 px-2 py-1 text-sm"
                  placeholder="精确匹配 lotNo"
                />
              </div>
              <button
                type="button"
                onClick={handleApplyMaterialFilter}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700"
              >
                查询
              </button>
            </div>
            {materialFilter.error && (
              <div className="text-sm text-red-600">
                {materialFilter.error.message}
              </div>
            )}
            <MaterialRowsTable rows={displayedMaterialRows} />
          </div>
        </section>
      )}

      {selectedSnapshotId && detail.error && (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-red-700">
          {detail.error.message}
        </div>
      )}
    </div>
  );
}
